import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Spinwheel from "../../components/common/Spinwheel";
import { useUserContext } from "../../context/userContext";
import {
  exportTallyZipApi,
  getTallyCompanySetupApi,
  getTallyMasterOptionsApi,
  saveTallyCompanySetupApi,
  saveTallyMasterMappingApi,
  validateTallySetupApi,
} from "../../utils/ApiEndpointFunctions";
import "./TallySetup.css";

const SECTIONS = [
  { value: "general", label: "General Setup", icon: "bi-gear" },
  { value: "masters", label: "Master Mapping", icon: "bi-diagram-3" },
  { value: "ledgers", label: "Ledger Mapping", icon: "bi-journal-text" },
  { value: "validation", label: "Validate Setup", icon: "bi-shield-check" },
  { value: "export", label: "Export Data", icon: "bi-download" },
];

const MASTER_TYPES = [
  { value: "vendor", label: "Vendor/Sub-contractor" },
  { value: "stock_item", label: "Stock Item" },
  { value: "service", label: "Service" },
  { value: "unit", label: "Unit" },
  { value: "site", label: "Site / Cost Centre" },
];

const MASTER_TABLE_COLUMNS = {
  vendor: [
    { fieldKey: "ledger_name", label: "Tally Ledger Name" },
    { fieldKey: "ledger_parent", label: "Parent Group" },
    { fieldKey: "gstin", label: "GSTIN" },
  ],
  stock_item: [
    { fieldKey: "stock_item_name", label: "Tally Stock Item" },
    { fieldKey: "stock_group", label: "Stock Group" },
    { fieldKey: "base_units", label: "Base Unit" },
  ],
  service: [
    { fieldKey: "service_ledger_name", label: "Tally Service Ledger" },
    { fieldKey: "service_parent", label: "Parent Group" },
    { fieldKey: "service_hsn", label: "SAC/HSN" },
  ],
  unit: [
    { fieldKey: "unit_symbol", label: "Unit Symbol" },
    { fieldKey: "unit_formal_name", label: "Formal Name" },
    { fieldKey: "decimal_places", label: "Decimal Places" },
  ],
  site: [
    { fieldKey: "cost_centre_name", label: "Cost Centre" },
    { fieldKey: "cost_category", label: "Cost Category" },
    { fieldKey: "godown_name", label: "Godown/Location" },
  ],
};

const getResolvedValue = (field) =>
  field.useMms ? field.mmsValue : field.tallyValue;

const getCurrentMonthRange = () => {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    from: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: formatDate(today),
  };
};

const updateMappingField = (fields, fieldKey, changes) =>
  fields.map((field) =>
    field.fieldKey === fieldKey ? { ...field, ...changes } : field
  );

const getMasterFieldValue = (option, fieldKey) => {
  const field = option.fields?.find((item) => item.fieldKey === fieldKey);
  return field ? getResolvedValue(field) : "";
};

const ConfiguredBadge = ({ status }) => {
  const normalizedStatus = ["yes", "invalid"].includes(status)
    ? status
    : "no";
  const label =
    normalizedStatus === "yes"
      ? "Yes"
      : normalizedStatus === "invalid"
      ? "Invalid"
      : "No";

  return (
    <span className={`tally-configured-badge is-${normalizedStatus}`}>
      {label}
    </span>
  );
};

const MappingTable = ({ fields, setFields, saving }) => {
  const { langPreference } = useUserContext();

  const handleUseMms = (field, checked) => {
    const value = checked ? field.mmsValue : field.tallyValue;
    setFields((currentFields) =>
      updateMappingField(currentFields, field.fieldKey, {
        useMms: checked,
        tallyValue: value,
        isMissing: field.required && !value?.toString().trim(),
      })
    );
  };

  const handleTallyValue = (field, value) => {
    setFields((currentFields) =>
      updateMappingField(currentFields, field.fieldKey, {
        tallyValue: value,
        isMissing: field.required && !value.trim(),
      })
    );
  };

  return (
    <div className="tally-mapping-table">
      <div className="tally-table-head">
        <div>{langPreference?.manage_my_site || "ManageMySite"}</div>
        <div>{langPreference?.tally || "Tally"}</div>
        <div>{langPreference?.same_as_mms || "Same as MMS"}</div>
      </div>

      {fields.map((field) => (
        <div
          className={`tally-mapping-row ${field.isMissing ? "has-error" : ""}`}
          key={field.fieldKey}
        >
          <div className="tally-source-cell">
            <div className="tally-field-label">
              {field.mmsLabel}
              {field.required && <span className="req-error"> *</span>}
            </div>
            <div
              className={`tally-source-value ${
                field.mmsValue ? "" : "is-empty"
              }`}
            >
              {field.mmsValue ||
                langPreference?.not_available ||
                "Not available"}
            </div>
          </div>

          <div className="tally-target-cell">
            <label htmlFor={`tally-${field.fieldKey}`}>
              {field.tallyLabel}
              {field.required && <span className="req-error"> *</span>}
              {!field.required && field.requirementNote && (
                <span className="tally-condition"> {field.requirementNote}</span>
              )}
            </label>
            {field.inputType === "select" ? (
              <select
                id={`tally-${field.fieldKey}`}
                className="form-select"
                value={getResolvedValue(field) || ""}
                disabled={field.useMms || saving}
                onChange={(event) =>
                  handleTallyValue(field, event.target.value)
                }
              >
                {(field.options || []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`tally-${field.fieldKey}`}
                type={field.inputType || "text"}
                className="form-control"
                value={getResolvedValue(field) || ""}
                disabled={field.useMms || saving}
                onChange={(event) =>
                  handleTallyValue(field, event.target.value)
                }
              />
            )}
            {field.isMissing && (
              <div className="tally-field-error">
                {field.tallyLabel} is required.
              </div>
            )}
          </div>

          <div className="tally-same-cell">
            {field.canUseMms ? (
              <input
                className="form-check-input"
                type="checkbox"
                aria-label={`Use ManageMySite value for ${field.tallyLabel}`}
                checked={field.useMms}
                disabled={saving}
                onChange={(event) =>
                  handleUseMms(field, event.target.checked)
                }
              />
            ) : (
              <span
                className="tally-system-value"
                title="This value is specific to Tally."
              >
                <i className="bi bi-pencil-square"></i>
                {langPreference?.tally_value || "Tally value"}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const TallySetup = () => {
  const { langPreference } = useUserContext();
  const [activeSection, setActiveSection] = useState("general");
  const [company, setCompany] = useState(null);
  const [sites, setSites] = useState([]);
  const [generalFields, setGeneralFields] = useState([]);
  const [ledgerFields, setLedgerFields] = useState([]);
  const [bankMappings, setBankMappings] = useState([]);
  const [entityType, setEntityType] = useState("vendor");
  const [options, setOptions] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [masterFields, setMasterFields] = useState([]);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportSiteIds, setExportSiteIds] = useState([]);
  const [exportModules, setExportModules] = useState([
    "purchase_orders",
    "challans",
    "vouchers",
  ]);
  const [includeMasters, setIncludeMasters] = useState(true);
  const currentMonth = getCurrentMonthRange();
  const [exportFromDate, setExportFromDate] = useState(currentMonth.from);
  const [exportToDate, setExportToDate] = useState(currentMonth.to);

  const getCompanySetup = async () => {
    setLoading(true);
    try {
      const response = await getTallyCompanySetupApi();
      const data = response?.data?.data || {};
      setCompany(data.company || null);
      const companySites = data.sites || [];
      setSites(companySites);
      setExportSiteIds((currentSiteIds) =>
        currentSiteIds.length
          ? currentSiteIds.filter((id) =>
              companySites.some((site) => site.id === id)
            )
          : companySites.map((site) => site.id)
      );
      setGeneralFields(data.generalFields || []);
      setLedgerFields(data.ledgerFields || []);
      setBankMappings(data.bankMappings || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to load Tally Setup."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCompanySetup();
  }, []);

  const getMasterOptions = async (type) => {
    setLoadingOptions(true);
    setOptions([]);
    setSelectedEntity(null);
    setMasterFields([]);
    try {
      const response = await getTallyMasterOptionsApi(type);
      setOptions(response?.data?.data?.options || []);
    } catch (error) {
      setOptions([]);
      toast.error(
        error?.response?.data?.message || "Unable to load master records."
      );
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (activeSection === "masters") {
      getMasterOptions(entityType);
    }
  }, [activeSection, entityType]);

  const getMapping = (option) => {
    setSelectedEntity(option);
    setMasterFields(option.fields || []);
  };

  const cancelMasterMapping = () => {
    if (saving) return;
    setSelectedEntity(null);
    setMasterFields([]);
  };

  const updateInlineMasterField = (fieldKey, value) => {
    setMasterFields((currentFields) =>
      updateMappingField(currentFields, fieldKey, {
        useMms: false,
        tallyValue: value,
        isMissing:
          currentFields.find((field) => field.fieldKey === fieldKey)
            ?.required && !value.toString().trim(),
      })
    );
  };

  const validateRequiredFields = (fields) => {
    const missing = fields.find(
      (field) =>
        field.required && !getResolvedValue(field)?.toString().trim()
    );
    if (missing) {
      const fieldLabel =
        missing.tallyLabel ||
        missing.fieldKey
          ?.replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase());
      toast.error(`${fieldLabel || "This field"} is required.`);
      return false;
    }
    return true;
  };

  const serializeFields = (fields) =>
    fields.map((field) => ({
      fieldKey: field.fieldKey,
      useMms: field.useMms,
      tallyValue: field.useMms ? "" : field.tallyValue,
    }));

  const saveCompanySection = async (section) => {
    const fields = section === "general" ? generalFields : ledgerFields;
    if (!validateRequiredFields(fields)) return;

    setSaving(true);
    try {
      const response = await saveTallyCompanySetupApi({
        section,
        fields: serializeFields(fields),
        ...(section === "ledgers" && {
          bankMappings: bankMappings.map((bank) => ({
            mmsBankName: bank.mmsBankName,
            useMms: bank.useMms,
            tallyLedgerName: bank.useMms ? "" : bank.tallyLedgerName,
          })),
        }),
      });
      if (section === "general") {
        setGeneralFields(response?.data?.data?.fields || fields);
      } else {
        setLedgerFields(response?.data?.data?.fields || fields);
        setBankMappings(
          response?.data?.data?.bankMappings || bankMappings
        );
      }
      setValidation(null);
      toast.success(response?.data?.message || "Tally setup saved.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to save Tally setup."
      );
    } finally {
      setSaving(false);
    }
  };

  const saveMasterMapping = async () => {
    if (!selectedEntity || !validateRequiredFields(masterFields)) return;

    setSaving(true);
    try {
      const response = await saveTallyMasterMappingApi({
        entityType,
        entityId: selectedEntity.value,
        fields: serializeFields(masterFields),
      });
      const savedFields = response?.data?.data?.fields || masterFields;
      setMasterFields(savedFields);
      setOptions((currentOptions) =>
        currentOptions.map((option) =>
          option.value === selectedEntity.value
            ? {
                ...option,
                isConfigured: true,
                configurationStatus: "yes",
                fields: savedFields,
              }
            : option
        )
      );
      setSelectedEntity(null);
      setMasterFields([]);
      setValidation(null);
      toast.success(response?.data?.message || "Tally mapping saved.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to save the Tally mapping."
      );
    } finally {
      setSaving(false);
    }
  };

  const updateBankMapping = (bankName, changes) => {
    setBankMappings((currentBanks) =>
      currentBanks.map((bank) =>
        bank.mmsBankName === bankName ? { ...bank, ...changes } : bank
      )
    );
  };

  const runValidation = async () => {
    setLoading(true);
    try {
      const response = await validateTallySetupApi();
      setValidation(response?.data?.data || null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to validate Tally setup."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleExportSite = (siteId) => {
    setExportSiteIds((currentIds) =>
      currentIds.includes(siteId)
        ? currentIds.filter((id) => id !== siteId)
        : [...currentIds, siteId]
    );
  };

  const toggleExportModule = (module) => {
    setExportModules((currentModules) =>
      currentModules.includes(module)
        ? currentModules.filter((value) => value !== module)
        : [...currentModules, module]
    );
  };

  const downloadTallyExport = async () => {
    if (!exportSiteIds.length) {
      toast.error("Select at least one site.");
      return;
    }
    if (!exportModules.length) {
      toast.error("Select at least one module.");
      return;
    }
    if (!exportFromDate || !exportToDate) {
      toast.error("Select the export date range.");
      return;
    }
    if (exportFromDate > exportToDate) {
      toast.error("From Date cannot be after To Date.");
      return;
    }

    setExporting(true);
    try {
      const response = await exportTallyZipApi({
        siteIds: exportSiteIds,
        modules: exportModules,
        fromDate: exportFromDate,
        toDate: exportToDate,
        includeMasters,
      });
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const responseFileName = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = responseFileName || `tally-export-${exportToDate}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Tally export downloaded.");
    } catch (error) {
      let message = "Unable to create the Tally export.";
      if (error?.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await error.response.data.text());
          message = payload?.message || message;
        } catch (_) {
          // Keep the fallback when the server did not return JSON.
        }
      } else {
        message = error?.response?.data?.message || message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (activeSection === "validation" && !validation) {
      runValidation();
    }
  }, [activeSection]);

  const renderPanelHeader = (title, description, badge) => (
    <div className="tally-mapping-header">
      <div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
      {badge && <span className="tally-record-type">{badge}</span>}
    </div>
  );

  const renderSaveFooter = (note, onSave, buttonText) => (
    <div className="tally-actions">
      <div className="tally-save-note">
        <i className="bi bi-info-circle"></i>
        {note}
      </div>
      <button
        type="button"
        className="create-order"
        disabled={saving}
        onClick={onSave}
      >
        {saving ? langPreference?.saving || "Saving..." : buttonText}
      </button>
    </div>
  );

  if (loading && !company) {
    return (
      <div className="main-content tally-loading">
        <Spinwheel />
      </div>
    );
  }

  return (
    <div className="main-content tally-setup-page">
      <div className="container-fluid">
        <div className="dash-nav1 tally-setup-heading">
          <div>
            <div className="tally-setup-eyebrow">
              {langPreference?.admin || "Admin"}
            </div>
            <h3 className="mb-1 font-blue">
              {langPreference?.tally_setup || "Tally Setup"}
            </h3>
            <p className="mb-0">
              Configure the values required to generate TallyPrime-compatible
              master and voucher JSON.
            </p>
          </div>
          {company && (
            <div className="tally-company-chip">
              <span>ManageMySite Company</span>
              <strong>{company.name}</strong>
            </div>
          )}
        </div>

        <div className="tally-section-nav">
          {SECTIONS.map((section, index) => (
            <button
              type="button"
              key={section.value}
              className={activeSection === section.value ? "active" : ""}
              onClick={() => setActiveSection(section.value)}
            >
              <span className="tally-step-number">{index + 1}</span>
              <i className={`bi ${section.icon}`}></i>
              {section.label}
            </button>
          ))}
        </div>

        {activeSection === "general" && (
          <div className="tally-mapping-panel">
            {renderPanelHeader(
              "Company and Export Defaults",
              "Confirm the exact Tally company and voucher names. Technical JSON import variables are generated automatically.",
              "Step 1"
            )}
            <MappingTable
              fields={generalFields}
              setFields={setGeneralFields}
              saving={saving}
            />
            {renderSaveFooter(
              "The Tally company name must exactly match the company loaded in TallyPrime.",
              () => saveCompanySection("general"),
              "Save General Setup"
            )}
          </div>
        )}

        {activeSection === "masters" && (
          <>
            <div className="tally-selector-panel">
              <div className="tally-selector-field">
                <label htmlFor="tallyType">Master Type</label>
                <select
                  id="tallyType"
                  className="form-select"
                  value={entityType}
                  disabled={loadingOptions || saving}
                  onChange={(event) => setEntityType(event.target.value)}
                >
                  {MASTER_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tally-master-count">
                <span>Records</span>
                <strong>{options.length}</strong>
              </div>
            </div>

            {loadingOptions ? (
              <div className="tally-loading">
                <Spinwheel />
              </div>
            ) : options.length ? (
              <div className="tally-master-list-panel">
                <div className="tally-master-list-heading">
                  <div>
                    <h4>
                      {
                        MASTER_TYPES.find((type) => type.value === entityType)
                          ?.label
                      }
                    </h4>
                    <p>
                      Review each record and configure its Tally mapping
                      individually.
                    </p>
                  </div>
                </div>
                <div className="tally-master-list-scroll">
                  <table className="tally-master-list-table">
                    <thead>
                      <tr>
                        <th>ManageMySite Record</th>
                        <th>Type</th>
                        {MASTER_TABLE_COLUMNS[entityType].map((column) => (
                          <th key={column.fieldKey}>{column.label}</th>
                        ))}
                        <th className="tally-centered-column">Configured</th>
                        <th className="tally-action-column">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.map((option) => (
                        <tr
                          className={
                            selectedEntity?.value === option.value
                              ? "is-selected"
                              : ""
                          }
                          key={option.value}
                        >
                          <td>
                            <strong>{option.label}</strong>
                          </td>
                          <td>{option.subtype || "-"}</td>
                          {MASTER_TABLE_COLUMNS[entityType].map((column) => {
                            const isEditing =
                              selectedEntity?.value === option.value;
                            const editableField = masterFields.find(
                              (field) => field.fieldKey === column.fieldKey
                            );
                            const displayValue = getMasterFieldValue(
                              option,
                              column.fieldKey
                            );

                            return (
                              <td key={column.fieldKey}>
                                {isEditing && editableField ? (
                                  editableField.inputType === "select" ? (
                                    <select
                                      className={`form-select tally-inline-input ${
                                        editableField.isMissing
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      value={getResolvedValue(editableField) || ""}
                                      disabled={saving}
                                      onChange={(event) =>
                                        updateInlineMasterField(
                                          column.fieldKey,
                                          event.target.value
                                        )
                                      }
                                    >
                                      {(editableField.options || []).map(
                                        (fieldOption) => (
                                          <option
                                            key={fieldOption}
                                            value={fieldOption}
                                          >
                                            {fieldOption}
                                          </option>
                                        )
                                      )}
                                    </select>
                                  ) : (
                                    <input
                                      className={`form-control tally-inline-input ${
                                        editableField.isMissing
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      type={editableField.inputType || "text"}
                                      value={getResolvedValue(editableField) || ""}
                                      disabled={saving}
                                      onChange={(event) =>
                                        updateInlineMasterField(
                                          column.fieldKey,
                                          event.target.value
                                        )
                                      }
                                    />
                                  )
                                ) : (
                                  displayValue || (
                                    <span className="tally-empty-value">
                                      Not set
                                    </span>
                                  )
                                )}
                              </td>
                            );
                          })}
                          <td className="tally-centered-column">
                            <ConfiguredBadge
                              status={option.configurationStatus}
                            />
                          </td>
                          <td className="tally-action-column">
                            {selectedEntity?.value === option.value ? (
                              <div className="tally-inline-actions">
                                <button
                                  type="button"
                                  className="tally-row-save"
                                  title="Save configuration"
                                  aria-label="Save configuration"
                                  disabled={saving}
                                  onClick={saveMasterMapping}
                                >
                                  <i className="bi bi-check-lg"></i>
                                </button>
                                <button
                                  type="button"
                                  className="tally-row-cancel"
                                  title="Cancel configuration"
                                  aria-label="Cancel configuration"
                                  disabled={saving}
                                  onClick={cancelMasterMapping}
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="tally-configure-button"
                                disabled={saving}
                                onClick={() => getMapping(option)}
                              >
                                Configure
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="tally-empty-state">
                <div className="tally-empty-icon">
                  <i className="bi bi-diagram-3"></i>
                </div>
                <h4>No records found</h4>
                <p>There are no active records for this master type.</p>
              </div>
            )}

          </>
        )}

        {activeSection === "ledgers" && (
          <div className="tally-mapping-panel">
            {renderPanelHeader(
              "Accounting Ledgers",
              "Enter the exact ledger names already used in TallyPrime. These names are referenced by exported vouchers.",
              "Step 3"
            )}
            <MappingTable
              fields={ledgerFields}
              setFields={setLedgerFields}
              saving={saving}
            />

            <div className="tally-bank-section">
              <div className="tally-bank-heading">
                <div>
                  <h5>Bank Ledger Mapping</h5>
                  <p>
                    Each bank name used in a non-cash MMS voucher must resolve to
                    a Tally Bank Ledger.
                  </p>
                </div>
                <span>{bankMappings.length} bank(s)</span>
              </div>

              {bankMappings.length ? (
                <div className="tally-bank-table">
                  <div className="tally-bank-table-head">
                    <span>MMS Bank Name</span>
                    <span>Tally Bank Ledger</span>
                    <span>Same as MMS</span>
                  </div>
                  {bankMappings.map((bank) => (
                    <div className="tally-bank-row" key={bank.mmsBankName}>
                      <strong>{bank.mmsBankName}</strong>
                      <input
                        className="form-control"
                        value={
                          bank.useMms
                            ? bank.mmsBankName
                            : bank.tallyLedgerName || ""
                        }
                        disabled={bank.useMms || saving}
                        onChange={(event) =>
                          updateBankMapping(bank.mmsBankName, {
                            tallyLedgerName: event.target.value,
                            isMissing: !event.target.value.trim(),
                          })
                        }
                      />
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={bank.useMms}
                        disabled={saving}
                        onChange={(event) =>
                          updateBankMapping(bank.mmsBankName, {
                            useMms: event.target.checked,
                            tallyLedgerName: event.target.checked
                              ? bank.mmsBankName
                              : bank.tallyLedgerName,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tally-bank-empty">
                  No non-cash bank names are currently used in MMS vouchers.
                </div>
              )}
            </div>

            {renderSaveFooter(
              "Ledger names must match Tally exactly, including spaces and spelling.",
              () => saveCompanySection("ledgers"),
              "Save Ledger Setup"
            )}
          </div>
        )}

        {activeSection === "validation" && (
          <div className="tally-validation-panel">
            <div className="tally-validation-top">
              <div>
                <div className="tally-setup-eyebrow">Final Check</div>
                <h4>Export Readiness</h4>
                <p>
                  Check required configuration before JSON export is enabled.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={runValidation}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Check Again
              </button>
            </div>

            {loading ? (
              <div className="tally-loading">
                <Spinwheel />
              </div>
            ) : validation && (
              <>
                <div
                  className={`tally-readiness ${
                    validation.isReady ? "is-ready" : "needs-attention"
                  }`}
                >
                  <i
                    className={`bi ${
                      validation.isReady
                        ? "bi-check-circle-fill"
                        : "bi-exclamation-triangle-fill"
                    }`}
                  ></i>
                  <div>
                    <strong>
                      {validation.isReady
                        ? "Required setup is ready"
                        : `${validation.issues.length} item(s) need attention`}
                    </strong>
                    <span>
                      {validation.isReady
                        ? "The configured values can be used by the JSON exporter."
                        : "Complete the fields below before exporting."}
                    </span>
                  </div>
                </div>

                {validation.issues.length > 0 && (
                  <div className="tally-issue-list">
                    {validation.issues.map((issue, index) => (
                      <div className="tally-issue" key={`${issue.field}-${index}`}>
                        <span>{issue.section}</span>
                        <strong>{issue.field}</strong>
                        <p>{issue.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="tally-validation-grid">
                  <div>
                    <h5>Saved Master Mappings</h5>
                    {(validation.mappingCounts || []).map((count) => (
                      <div className="tally-count-row" key={count.entityType}>
                        <span>
                          {MASTER_TYPES.find(
                            (type) => type.value === count.entityType
                          )?.label || count.entityType}
                        </span>
                        <strong>{count.mapped}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeSection === "export" && (
          <div className="tally-export-panel">
            {renderPanelHeader(
              "Export Tally Data",
              "Download approved records from one or more sites as a TallyPrime JSON package.",
              "Step 5"
            )}

            <div className="tally-export-content">
              <section className="tally-export-section">
                <div className="tally-export-section-heading">
                  <div>
                    <h5>Sites</h5>
                    <p>Select one site, several sites, or the full company.</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() =>
                      setExportSiteIds(
                        exportSiteIds.length === sites.length
                          ? []
                          : sites.map((site) => site.id)
                      )
                    }
                    disabled={exporting || !sites.length}
                  >
                    {exportSiteIds.length === sites.length
                      ? "Clear All"
                      : "Select All"}
                  </button>
                </div>

                <div className="tally-site-selection">
                  {sites.map((site) => (
                    <label
                      className={`tally-site-option ${
                        exportSiteIds.includes(site.id) ? "is-selected" : ""
                      }`}
                      key={site.id}
                    >
                      <input
                        type="checkbox"
                        checked={exportSiteIds.includes(site.id)}
                        disabled={exporting}
                        onChange={() => toggleExportSite(site.id)}
                      />
                      <span>{site.name}</span>
                    </label>
                  ))}
                </div>
              </section>

              <div className="tally-export-grid">
                <section className="tally-export-section">
                  <div className="tally-export-section-heading">
                    <div>
                      <h5>Approval Date Range</h5>
                      <p>Records are selected by their approval date across every chosen module and site.</p>
                    </div>
                  </div>
                  <div className="tally-export-date-grid">
                    <label>
                      From Date
                      <input
                        className="form-control"
                        type="date"
                        value={exportFromDate}
                        disabled={exporting}
                        onChange={(event) => setExportFromDate(event.target.value)}
                      />
                    </label>
                    <label>
                      To Date
                      <input
                        className="form-control"
                        type="date"
                        value={exportToDate}
                        disabled={exporting}
                        onChange={(event) => setExportToDate(event.target.value)}
                      />
                    </label>
                  </div>
                </section>

                <section className="tally-export-section">
                  <div className="tally-export-section-heading">
                    <div>
                      <h5>Modules</h5>
                      <p>Only approved and active records are exported.</p>
                    </div>
                  </div>
                  <div className="tally-module-selection">
                    {[
                      ["purchase_orders", "Purchase Orders"],
                      ["challans", "Challans / Receipt Notes"],
                      ["vouchers", "Payments / Receipts"],
                    ].map(([value, label]) => (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={exportModules.includes(value)}
                          disabled={exporting}
                          onChange={() => toggleExportModule(value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </section>
              </div>

              <div className="tally-export-footer">
                <label className="tally-include-masters">
                  <input
                    type="checkbox"
                    checked={includeMasters}
                    disabled={exporting}
                    onChange={(event) => setIncludeMasters(event.target.checked)}
                  />
                  <span>
                    <strong>Include required masters</strong>
                    Recommended so referenced sites, vendors, items, services,
                    and units are available in Tally.
                  </span>
                </label>
                <button
                  type="button"
                  className="create-order tally-export-button"
                  disabled={exporting || !sites.length}
                  onClick={downloadTallyExport}
                >
                  <i className={`bi ${exporting ? "bi-hourglass-split" : "bi-file-earmark-zip"}`}></i>
                  {exporting ? "Preparing Export..." : "Export Tally ZIP"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TallySetup;
